import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { Container } from '../../../ui/gridSystem';
import {
  CTA,
  CustomDatePicker,
  CustomInput,
  CustomSelect,
  NoResult,
  Text,
} from '../../../ui/atoms';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import { LOGS_LIST_GET } from '../../redux/actions/supervisorStats';
import { StickyFiltersWrapper } from '../../../ui/atoms/CTA/style';
import { MODAL_OPEN } from '../../redux/actions';
import ModalFiltersLogs, {
  ModalFiltersLogsId,
} from '../../../ui/atoms/Modals/ModalFiltersLogs';
import {
  CardsWrapper,
  FiltersWrapper,
  Legend,
  LegendAndCard,
  LogCard,
  SortingWrapper,
} from './style';
import {
  getFilterLabel,
  getFilterOptions,
  getLabelByRole,
} from '../../../utils/labels';
import SortBy from '../../../ui/atoms/SortBy';
import theme from '../../../ui/theme';
import Pagination from '../../../ui/atoms/Pagination';
import { timestampToDate } from '../../../utils/common';

const customParseFormat = require('dayjs/plugin/customParseFormat');

dayjs.extend(customParseFormat);

const LogsList = ({
  mediaIsPhone,
  list,
  filters,
  pager,
  getLogsList,
  modalOpen,
}) => {
  // filters
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isSearchAvailable, setIsSearchAvailable] = useState(false);
  const [userHasSearched, setUserHasSearched] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState(false);

  useEffect(() => {
    getLogsList({ filters_only: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { start_date, end_date, table, roles, name, lastname, email, ffoo } =
      selectedFilters;
    let valid = Boolean(start_date && end_date && table && roles);

    if (advancedSearch) {
      valid = valid && Boolean(name || lastname || email || ffoo);
    }
    setIsSearchAvailable(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const handleGetLogsList = () => {
    const body = { ...selectedFilters };
    if (!advancedSearch) {
      delete body.name;
      delete body.lastname;
      delete body.email;
      delete body.ffoo;
    }
    if (isSearchAvailable) getLogsList(body);
  };

  useEffect(() => {
    handleGetLogsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters?.sort_by, selectedFilters?.items_per_page]);

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => getLogsList({ ...selectedFilters, page });
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  const toggleAdvancedSearch = () => {
    const newFilters = { ...selectedFilters };
    if (!advancedSearch) newFilters.search_text = '';

    setSelectedFilters(newFilters);
    setAdvancedSearch(!advancedSearch);
  };

  return (
    <>
      <Container>
        <Text type="h1" mb={8}>
          Elenco Log
        </Text>
        <Text type="b3" mb={40}>
          Sezione dedicata alla visualizzazione di tutte le azioni compiute
          all’interno della piattaforma da tutti gli utenti.
        </Text>
        {mediaIsPhone ? (
          <StickyFiltersWrapper mb={16} stickyPosition={0}>
            <CustomInput
              placeholder="Cosa vuoi cercare?..."
              isSearch
              value=""
              icon="search-sm"
              isClearable
              onChange={(v) =>
                setSelectedFilters({ ...selectedFilters, search_text: v })
              }
            />
            <CTA
              classes="small-btn"
              btnText="Filtri"
              type="secondary"
              fullsize
              onClick={() =>
                modalOpen(ModalFiltersLogsId, {
                  filters,
                  activeFilters: selectedFilters,
                })
              }
            />
          </StickyFiltersWrapper>
        ) : (
          <>
            <FiltersWrapper>
              <CustomInput
                size="full"
                placeholder="Cosa vuoi cercare?..."
                isSearch
                value={selectedFilters.search_text || ''}
                icon="search-sm"
                isClearable
                onChange={(v) =>
                  setSelectedFilters({ ...selectedFilters, search_text: v })
                }
                onClickIcon={() => handleGetLogsList()}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') handleGetLogsList();
                }}
                disabled={advancedSearch}
              />
              <CustomDatePicker
                id="start-log-dp"
                placeholder="Data di inizio"
                onChange={(date) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    start_date: date && dayjs(date).format('DD-MM-YYYY'),
                  })
                }
                maxDate={
                  selectedFilters?.end_date
                    ? new Date(
                        dayjs(selectedFilters?.end_date, 'DD-MM-YYYY').format(
                          'YYYY-MM-DD'
                        )
                      )
                    : new Date()
                }
              />
              <CustomDatePicker
                id="end-log-dp"
                placeholder="Data di fine"
                onChange={(date) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    end_date: date && dayjs(date).format('DD-MM-YYYY'),
                  })
                }
                minDate={
                  selectedFilters?.start_date
                    ? new Date(
                        dayjs(selectedFilters?.start_date, 'DD-MM-YYYY').format(
                          'YYYY-MM-DD'
                        )
                      )
                    : undefined
                }
                maxDate={new Date()}
              />
              {(Object.keys(filters) || []).map((key, i) => {
                if (key === 'ffoo') return null;
                return (
                  <CustomSelect
                    size="full"
                    key={`filter-${i}`}
                    wrapperId={`filter-${key}`}
                    placeholder={getFilterLabel(key)}
                    options={getFilterOptions(filters, key)}
                    onChange={(value) => handleOnChange({ key, value })}
                    isClearable
                  />
                );
              })}
              <div className="full-width">
                <CTA
                  type="tertiary"
                  btnText="Ricerca avanzata"
                  icon={advancedSearch ? 'chevron-up' : 'chevron-down'}
                  onClick={toggleAdvancedSearch}
                />
              </div>
              {advancedSearch && (
                <>
                  <CustomInput
                    size="full"
                    placeholder="Nome"
                    value={selectedFilters.name}
                    isClearable
                    onChange={(v) =>
                      setSelectedFilters({ ...selectedFilters, name: v })
                    }
                  />
                  <CustomInput
                    size="full"
                    placeholder="Cognome"
                    value={selectedFilters.lastname}
                    isClearable
                    onChange={(v) =>
                      setSelectedFilters({ ...selectedFilters, lastname: v })
                    }
                  />
                  <CustomInput
                    size="full"
                    placeholder="Email"
                    value={selectedFilters.email}
                    isClearable
                    onChange={(v) =>
                      setSelectedFilters({ ...selectedFilters, email: v })
                    }
                  />
                  <CustomSelect
                    size="full"
                    placeholder="Forza dell'ordine"
                    options={getFilterOptions(filters, 'ffoo')}
                    onChange={(value) => handleOnChange({ key: 'ffoo', value })}
                    isClearable
                  />
                </>
              )}
              <CTA
                type="primary"
                disabled={!isSearchAvailable}
                btnText="Cerca"
                onClick={() => {
                  handleGetLogsList();
                  setUserHasSearched(true);
                }}
              />
            </FiltersWrapper>
            {userHasSearched && (
              <SortingWrapper>
                <Text type="b3">{pager?.total_items ?? 0} log</Text>
                <SortBy
                  options={[
                    { label: 'Dal più recente', value: 'created_desc' },
                    { label: 'Dal meno recente', value: 'created_asc' },
                  ]}
                  onChange={(v) => {
                    setSelectedFilters({
                      ...selectedFilters,
                      sort_by: v?.value,
                    });
                  }}
                  disabled={!isSearchAvailable}
                />
              </SortingWrapper>
            )}
          </>
        )}
        {userHasSearched ? (
          <>
            {list.length > 0 ? (
              <CardsWrapper hasPaginator={pager?.number_of_pages > 1}>
                {(list || []).map((log, i) => (
                  <LegendAndCard
                    key={`log-${i}`}
                    gridTemplateColumns="3fr 1.2fr 0.6fr 0.8fr 0.7fr 1fr"
                    gridTemplateRows="48px repeat(5, 24px)"
                  >
                    {(i === 0 || mediaIsPhone) && (
                      <Legend>
                        <Text type="statistics-heading">Descrizione log</Text>
                        <Text type="statistics-heading">Nome utente</Text>
                        <Text type="statistics-heading">Matricola</Text>
                        <Text type="statistics-heading">Tipologia profilo</Text>
                        <Text type="statistics-heading">Indirizzo IP</Text>
                        <Text type="statistics-heading">Data e ora</Text>
                      </Legend>
                    )}
                    <LogCard>
                      <Text type="h3.5" className="line-clamp two-lines">
                        {log?.description}
                      </Text>
                      <Text type="b4">
                        {log?.name} {log?.lastname}
                      </Text>
                      <Text type="b4">{log?.matricola}</Text>
                      <Text type="b4" className="ellipsis">
                        {(log?.roles ?? [])
                          .map((role) => getLabelByRole(role))
                          .join(', ')}
                      </Text>
                      <Text type="b4" color={theme().colors.primaryDarkGray700}>
                        {log?.user_client_ip}
                      </Text>
                      <Text type="b4" color={theme().colors.primaryDarkGray700}>
                        {timestampToDate(log?.created, 'DD/MM/YYYY - HH:mm')}
                      </Text>
                    </LogCard>
                  </LegendAndCard>
                ))}
              </CardsWrapper>
            ) : (
              <NoResult />
            )}
            <Pagination
              pb={48}
              pager={pager}
              callBack={loadMore}
              setItemsPerPage={setItemsPerPage}
            />
          </>
        ) : (
          <Text type="b3" mb={32}>
            Per visualizzare i log imposta tutti i filtri
          </Text>
        )}
      </Container>
      {mediaIsPhone && (
        <ModalFiltersLogs
          onClick={(modalFilters) => {
            setSelectedFilters(modalFilters);
            setUserHasSearched(true);
          }}
        >
          <Text type="b3">Per visualizzare i log imposta tutti i filtri</Text>
        </ModalFiltersLogs>
      )}
    </>
  );
};

LogsList.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  list: PropTypes.array,
  filters: PropTypes.object,
  pager: PropTypes.object,
  //dispatch
  getLogsList: PropTypes.func,
  modalOpen: PropTypes.func,
};

export default connect(
  (state) => {
    const { list, filters, pager } = state.supervisorStats.lists.logs;
    return {
      list,
      filters,
      pager,
    };
  },
  (dispatch) => ({
    getLogsList: (filters) =>
      dispatch({ type: LOGS_LIST_GET._REQUEST, filters }),
    modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
  })
)(withMediaQueries(LogsList));
